import { Button, type TypeButtonProps } from "@sproutsocial/seeds-react-button";
import { useMenuToggleContext } from "../MenuContext";
import type React from "react";

export interface TypeMenuToggleButtonProps extends TypeButtonProps {}

/**
 * @link https://seeds.sproutsocial.com/components/seeds-react-menu/#menutogglebutton
 * @param props - MenuToggleButton accepts all the same props as Button.
 * @description MenuToggleButton extends the Button component to create a toggle for a menu.
 * @see {@link https://seeds.sproutsocial.com/components/button/ Button}
 * @see {@link https://seeds.sproutsocial.com/components/seeds-react-menu/#menulabel MenuLabel}
 */
export const MenuToggleButton = (props: TypeMenuToggleButtonProps) => {
  const { getToggleButtonProps, isListbox, getDropdownProps, ref, ariaProps } =
    useMenuToggleContext();

  const {
    onClick: userOnClick,
    onKeyDown: userOnKeyDown,
    onBlur: userOnBlur,
    ...restProps
  } = props;

  const defaultDropdownProps = {
    ref,
    onClick: userOnClick,
    onKeyDown: userOnKeyDown,
    onBlur: userOnBlur,
  };

  const dropdownProps =
    getDropdownProps?.({
      ...defaultDropdownProps,
      preventKeyAction: true,
    }) ?? defaultDropdownProps;

  const toggleButtonProps = getToggleButtonProps?.({
    ...dropdownProps,
    refKey: "innerRef",
    ...ariaProps,
    ...(isListbox ? {} : { role: "button", "aria-haspopup": "menu" }),
  });

  // Clean up default downshift aria attributes
  // If there is not an active descendant, remove the attribute
  if (toggleButtonProps && toggleButtonProps["aria-activedescendant"] === "") {
    toggleButtonProps["aria-activedescendant"] = undefined;
  }
  // If the menu is closed, remove the aria-controls attribute
  if (toggleButtonProps && toggleButtonProps["aria-expanded"] === false) {
    // @ts-expect-error downshift types require aria-controls even though it is not required
    toggleButtonProps["aria-controls"] = undefined;
  }
  // Remove aria-labelledby, we add our own Label component
  if (toggleButtonProps) {
    toggleButtonProps["aria-labelledby"] = undefined;
  }

  return (
    <Button appearance="secondary" {...toggleButtonProps} {...restProps} />
  );
};
